import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowLeft, Eye, MessagesSquare, Trash2 } from "lucide-react";
import { Col, Row } from "../Common/Row";
import { EmptyState } from "../Common/EmptyState";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { ConfirmDialog } from "../Common/ConfirmDialog";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { Department } from "../../api/departmentsApi";
import { parseDocDeptId } from "../../api/documentsApi";
import type {
  ConversationDetail,
  ConversationDocument,
  Learning,
  LearningPatch,
} from "../../api/conversationKnowledgeApi";
import { PromoteVisibilityDialog } from "./PromoteVisibilityDialog";
import { LearningRow } from "./LearningRow";
import { usePortalScope } from "@/analytics/PortalScope";

const TIER_LABEL: Record<string, string> = {
  user: "Personal",
  department: "Department",
  company: "Company",
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

function docDate(doc: ConversationDocument): string {
  return formatDate(doc.last_synced_at ?? doc.created_at);
}

function learningCountLabel(n: number): string {
  return n === 1 ? "1 learning" : `${n} learnings`;
}

/** The Knowledge page's "User conversations" view (spec §7.3): the caller's
 *  visible conversation documents, and one opened with its summary and
 *  learnings. */
export function ConversationKnowledgeView({
  documents,
  selectedId,
  onSelect,
  onChanged,
  manageableDepts,
  shareDepts,
}: {
  documents: ConversationDocument[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  /** Refetch the list (after a delete, a visibility change, an edit). */
  onChanged: () => void;
  manageableDepts: Department[];
  shareDepts?: Department[];
}) {
  if (selectedId != null) {
    return (
      <ConversationDetailView
        documentId={selectedId}
        onBack={() => onSelect(null)}
        onChanged={onChanged}
        manageableDepts={manageableDepts}
        shareDepts={shareDepts}
      />
    );
  }
  if (documents.length === 0) {
    return (
      <div data-ph-scope="empty" style={{ display: "contents" }}>
        <EmptyState
          icon={MessagesSquare}
          title="No saved conversations yet"
          description="Conversations saved to knowledge appear here, with what the assistant learned from them."
        />
      </div>
    );
  }
  return (
    <Col gap={1}>
      {documents.map((doc) => (
        <WorkspaceCard key={doc.id} data-ph-scope="conversation">
          <Row align="center" gap={1.5}>
            <MessagesSquare size={20} aria-hidden style={{ flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => onSelect(doc.id)}
                data-ph="open"
                sx={{ fontWeight: 500, textAlign: "left", color: "text.primary" }}
              >
                {doc.title || doc.filename}
              </Link>
              <Row gap={1} align="center" sx={{ mt: 0.25 }}>
                <Chip size="small" variant="outlined" label={TIER_LABEL[doc.tier] ?? doc.tier} />
                <Typography variant="caption" color="text.secondary">
                  {learningCountLabel(doc.learning_count)}
                  {docDate(doc) ? ` · ${docDate(doc)}` : ""}
                </Typography>
              </Row>
            </Box>
          </Row>
        </WorkspaceCard>
      ))}
    </Col>
  );
}

function ConversationDetailView({
  documentId,
  onBack,
  onChanged,
  manageableDepts,
  shareDepts,
}: {
  documentId: number;
  onBack: () => void;
  onChanged: () => void;
  manageableDepts: Department[];
  shareDepts?: Department[];
}) {
  const { conversationKnowledge, documents } = useAuthedApi();
  const deleteScope = usePortalScope("conversation-delete_dialog")["data-ph-scope"];
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    if (!conversationKnowledge) return;
    setError(null);
    try {
      setDetail(await conversationKnowledge.get(documentId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [conversationKnowledge, documentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceLearning = (row: Learning) =>
    setDetail((d) =>
      d ? { ...d, learnings: d.learnings.map((l) => (l.id === row.id ? row : l)) } : d,
    );

  const back = (
    <Button startIcon={<ArrowLeft size={14} />} onClick={onBack} data-ph="back" sx={{ mb: 1 }}>
      All conversations
    </Button>
  );

  if (error) {
    return (
      <Box data-ph-scope="conversation">
        {back}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!detail) {
    return (
      <Box data-ph-scope="conversation">
        {back}
        <Row justify="center" sx={{ py: 4 }}>
          <CircularProgress size={24} aria-label="loading conversation" />
        </Row>
      </Box>
    );
  }

  const { document: doc, learnings } = detail;
  const label = doc.title || doc.filename;
  const editable = doc.can_write === true;

  return (
    <Box data-ph-scope="conversation">
      {back}
      <WorkspaceCard>
        <Row align="start" gap={1.5}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
            <Row gap={1} align="center" sx={{ mt: 0.5 }}>
              <Chip size="small" variant="outlined" label={TIER_LABEL[doc.tier] ?? doc.tier} />
              <Typography variant="caption" color="text.secondary">
                {docDate(doc)}
              </Typography>
              {/* The source chat opens for its author only; the API sends
                  session_id to nobody else. */}
              {doc.session_id && (
                <Link
                  component={RouterLink}
                  to={`/workspace?session=${encodeURIComponent(doc.session_id)}`}
                  variant="caption"
                  data-ph="open_chat"
                >
                  Open source chat
                </Link>
              )}
            </Row>
          </Box>
          {editable && (
            <Row gap={0.5}>
              <Tooltip title="Change visibility">
                <Button
                  size="small"
                  startIcon={<Eye size={14} />}
                  onClick={() => setVisibilityOpen(true)}
                  data-ph="open_visibility"
                >
                  Visibility
                </Button>
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  size="small"
                  color="error"
                  startIcon={<Trash2 size={14} />}
                  onClick={() => setConfirmDelete(true)}
                  data-ph="delete"
                >
                  Delete
                </Button>
              </Tooltip>
            </Row>
          )}
        </Row>

        <Typography variant="subtitle2" component="h3" sx={{ mt: 2, mb: 0.5 }}>
          Summary
        </Typography>
        <Typography variant="body2" color={doc.summary ? "text.primary" : "text.secondary"} sx={{ whiteSpace: "pre-wrap" }}>
          {doc.summary || "No summary available."}
        </Typography>

        <Typography variant="subtitle2" component="h3" sx={{ mt: 2, mb: 0.5 }}>
          What the assistant learned
        </Typography>
        {learnings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing was learned from this conversation.
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0 }} aria-label="learnings">
            {learnings.map((l) => (
              <LearningRow
                // Keyed by id only: the row stays mounted across its own edit
                // and retire (it reads every shown value from props), so no
                // state is set on an unmounted row.
                key={l.id}
                learning={l}
                editable={editable}
                onSave={async (patch: LearningPatch) => {
                  replaceLearning(await conversationKnowledge.editLearning(l.id, patch));
                }}
                onRetire={async () => {
                  replaceLearning(await conversationKnowledge.retireLearning(l.id));
                  onChanged(); // the list's learning count
                }}
              />
            ))}
          </Box>
        )}
      </WorkspaceCard>

      {editable && (
        <PromoteVisibilityDialog
          open={visibilityOpen}
          onClose={() => setVisibilityOpen(false)}
          documentId={doc.id}
          documentLabel={label}
          personal={doc.user_id != null}
          manageableDepts={manageableDepts}
          // Sharing reaches any department except the one that owns it.
          shareDepts={shareDepts?.filter((dep) => dep.id !== parseDocDeptId(doc.dept_id))}
          onChanged={() => {
            void load();
            onChanged();
          }}
        />
      )}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this conversation?"
        body={
          <>
            <strong>{label}</strong> and everything learned from it will be removed from
            knowledge. The chat itself is kept.
          </>
        }
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          await documents.remove(doc.id);
          setConfirmDelete(false);
          onBack();
          onChanged();
        }}
        onClose={() => setConfirmDelete(false)}
        scope={deleteScope}
      />
    </Box>
  );
}
